import { achievementsApi, type AchievementItem } from '@/api/achievements'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { getAchievementIcon } from '@/components/achievements/achievementIcons'
import { Card } from '@/components/ui/Card'
import { localizedAchievementTitle } from '@/i18n/achievementTitle'
import { useMinCopy } from '@/i18n/useUiLocale'
import { copy } from '@/lib/copy'
import { useQuery } from '@tanstack/react-query'

export function AchievementsPanel() {
  const query = useQuery({
    queryKey: ['achievements'],
    queryFn: () => achievementsApi.getAll(),
  })

  if (query.isLoading) {
    return (
      <Card className="flex h-40 items-center justify-center p-6">
        <LoadingSpinner />
      </Card>
    )
  }

  if (query.isError || !query.data) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Could not load achievements. Try again later.
      </Card>
    )
  }

  const unlockedCount = query.data.achievements.filter((item) => item.unlocked).length

  return (
    <Card className="p-6" data-testid="achievements-panel">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">{copy.achievements.heading}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{copy.achievements.subheading}</p>
        </div>
        <p className="text-sm font-medium text-primary">
          {unlockedCount}/{query.data.achievements.length}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {query.data.achievements.map((achievement) => (
          <AchievementBadge key={achievement.code} achievement={achievement} />
        ))}
      </div>
    </Card>
  )
}

function AchievementBadge({ achievement }: { achievement: AchievementItem }) {
  const { achievementTitles } = useMinCopy()
  const Icon = getAchievementIcon(achievement.icon)
  const unlocked = achievement.unlocked
  const title = localizedAchievementTitle(achievementTitles, achievement.code, achievement.title)

  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${
        unlocked
          ? 'border-primary/30 bg-primary/5'
          : 'border-border bg-muted/20 opacity-70'
      }`}
      data-testid={`achievement-${achievement.code}`}
      data-unlocked={unlocked ? 'true' : 'false'}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          unlocked ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{achievement.description}</span>
      </span>
    </div>
  )
}
